import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { COMPANY, waLink } from "@/lib/config";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/#services" },
  { label: "Products", to: "/products" },
  { label: "Blog", to: "/blog" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav py-3" : "py-5 bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img
            src={COMPANY.logoFull}
            alt="Digitify My Biz"
            className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) =>
            l.to.includes("#") ? (
              <a
                key={l.label}
                href={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={waLink("Hi Digitify My Biz! I'd like to grow my real estate business.")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-cta-whatsapp"
            className="inline-flex items-center gap-2 rounded-full bg-brand-blue hover:bg-brand-blueHover px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:scale-[1.03] active:scale-95 cyan-glow"
          >
            <MessageCircle className="h-4 w-4" />
            Talk to us
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden mx-4 mt-3 glass-panel rounded-2xl p-3" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) =>
            l.to.includes("#") ? (
              <a
                key={l.label}
                href={l.to}
                className="block px-4 py-3 text-slate-200 hover:text-white rounded-xl hover:bg-white/5"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                to={l.to}
                className="block px-4 py-3 text-slate-200 hover:text-white rounded-xl hover:bg-white/5"
              >
                {l.label}
              </Link>
            )
          )}
          <a
            href={waLink("Hi Digitify My Biz! I'd like to grow my real estate business.")}
            target="_blank"
            rel="noreferrer"
            className="mt-2 flex items-center justify-center gap-2 rounded-full bg-brand-blue px-5 py-3 font-semibold text-white"
          >
            <MessageCircle className="h-4 w-4" /> Talk to us
          </a>
        </div>
      )}
    </header>
  );
}
